import useDialog from 'hooks/common/useDialog';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PictureUploadData } from 'components/bottomsheet/chat/PictureUpload';

const TAG = '[PictureUpload]';
const useFreeBoom = false; // 로그 전송용 - 과거에 존재

interface CanUseBoomMode {
  canUse: boolean;
  reason: string;
}

interface NudityInfo {
  id: number;
  score: number;
  location: [number, number, number, number];
}

interface NudityObj {
  key: any; // G || N
  score: number; // 0~100
  type: 'genital' | 'naked' | 'normal';
}

interface PictureKeyAllocation {
  backend: 'fb' | 'fs';
  myKey: string;
  linkKey: string;
  fsRefs?: { senderRef: { id: string }; receiverRef: { id: string } };
}

interface PhotoUploadResult {
  downloadURL?: string;
  token?: string;
}

// boom 모드 이용 가능 여부 체크
const checkCanUseBoomMode = async (uid: string): Promise<CanUseBoomMode> => {
  if (getUserAppById(uid) === 'linkle') return { canUse: false, reason: 'linkle_user' };

  const isFriend = await FriendsDataManager.isFriendAsync(uid);
  if (!isFriend) return { canUse: false, reason: 'not_friend' };

  if (!ChatDataManager.hasRoom(uid)) return { canUse: false, reason: 'no_chat_room' };

  const hasMutualConversation: boolean = PageChatRoom.getHasMutualConversation();
  return { canUse: hasMutualConversation, reason: hasMutualConversation ? '' : 'no_mutual_messages' };
};

// NudityInfo -> NudityObj
const makeNudityObj = (nudityInfo: NudityInfo | null): NudityObj => {
  const nudity: NudityObj = {
    key: null,
    score: 0,
    type: 'normal',
  };

  if (nudityInfo) {
    if (nudityInfo.id === 1) {
      nudity.key = 'G';
      nudity.type = 'genital';
    } else if (nudityInfo.id === 2) {
      nudity.key = 'N';
      nudity.type = 'naked';
    }
    nudity.score = Math.round(nudityInfo.score * 100);
    nudity.key += nudity.score;
  }

  return nudity;
};

// 로그 전송
const sendPictureLog = (nudity: NudityObj) => {
  ba('picture', undefined, {
    action: 'send',
    fbUid: myinfo.fbUid,
    gender: myinfo.gender,
    nuditytype: nudity.type,
    nudityscore: nudity.score,
  });
};

// 로그 전송
const sendChatPictureLog = (nudity: NudityObj, token: string, fromNudge: boolean, useBoom: boolean) => {
  const isPrime = PrimeDataManager.getState() === 2;
  ba('boom_upload', undefined, {
    fbUid: myinfo.fbUid,
    gender: myinfo.gender,
    country: myinfo.country,
    swipeCnt: myinfo.swipeCnt,
    nuditytype: nudity.type,
    nudityscore: nudity.score,
    fromNudge: fromNudge,
    useBoom: useBoom,
    useFreeBoom: useFreeBoom,
    token: token,
    isPrime: isPrime,
  });
};

const usePictureUpload = (close: (id: string) => void, data: PictureUploadData) => {
  const { open: openDialog } = useDialog();
  const [isLoading, setIsLoading] = useState(false);
  const [boomSwitchOn, setBoomSwitchOn] = useState(false);
  const [canUseBoomModeState, setCanUseBoomModeState] = useState<CanUseBoomMode | null>(null);
  const [nudityObjState, setNudityObjState] = useState<NudityObj>({ key: null, score: 0, type: 'normal' });

  const boomPrice = myinfo.gender === 'male' ? Def.COIN_BOOM_MALE : Def.COIN_BOOM_FEMALE;
  const boomProduct = myinfo.gender === 'male' ? 'boom_male' : 'boom_female';

  const fromNudge = useRef(false); // 로그 전송용 - nudge 모달에서 boom 모드로 전환된 경우 true

  // boom 모드 스위치 변경 핸들러
  const handleBoomSwitchChange = useCallback(async () => {
    if (boomSwitchOn) {
      setBoomSwitchOn(false);
    } else {
      const { canUse, reason } = canUseBoomModeState ?? { canUse: false, reason: '' };
      BLog.log(TAG, 'canUseBoomMode() canUse:', canUse, 'reason:', reason);
      // Boom 모드 이용 불가 시 팝업 표시
      if (!canUse) {
        const msg = brsc.boom_disable_user;
        void openDialog({ description: msg });
        return;
      }
      // 코인 부족 시 팝업 표시
      if (MyDataManager.getCoin() < boomPrice) {
        CommonUI.showNeedCoinPopup(
          {
            feature: 'boom',
            title: brsc.boom_coin_popup_title,
            desc: brsc.boom_coin_popup_desc,
          },
          (result: boolean) => {
            if (result) {
              // 확인 버튼(코인샵 이동 등)으로 닫힌 경우에만 리액트 바텀시트 닫기
              close('PictureUpload');
            }
          }
        );
        return;
      }
      setBoomSwitchOn(true);
    }
  }, [boomPrice, boomSwitchOn, canUseBoomModeState, close, openDialog]);

  // 초기화
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      const { uid, blob } = data;
      BLog.log(TAG, 'blob.length', blob.length);

      const canUseBoomMode = await checkCanUseBoomMode(uid);
      setCanUseBoomModeState(canUseBoomMode);

      const nudityInfo = await NudityDetector.checkNudityImageAsync(blob);
      setNudityObjState(makeNudityObj(nudityInfo));

      // Boom 모드 이용 권유
      if (nudityInfo && (nudityInfo.id === 1 || nudityInfo.id === 2) && canUseBoomMode.canUse) {
        PageChatRoom.showBoomModal('nudge', () => {
          fromNudge.current = true;
          setBoomSwitchOn(true);
        });
      }
      setIsLoading(false);
    };

    init();
  }, [data]);

  // 사진 업로드
  const uploadPicture = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const { uid, no, blob, width, height } = data;
      const type = boomSwitchOn ? 'boom' : 'picture';
      const datekey = BUtil.getDateKey();

      // 채팅방 backend(fb/fs)를 식별하고 메시지 위치 키를 사전 할당한다.
      // 파일명에 좌표를 인코딩해 두면, 모더레이션 결과 NSFW/CSAM 일 때 aiwatcher 가
      // 양쪽 사용자의 메시지를 정확히 찾아 삭제할 수 있다.
      ChatDataManager.allocatePictureKeys(uid, (allocated: PictureKeyAllocation | null) => {
        if (!allocated) {
          reject(new Error('Failed to allocate message keys.'));
          return;
        }
        const key = makePictureKey(myinfo.fbUid, uid, datekey, nudityObjState?.key, allocated.backend, allocated.myKey, allocated.linkKey);
        const photo = {
          data: blob,
          dir: 'chatPicture/' + myinfo.fbUid + '/' + datekey,
          fname: key + '.jpg',
          isPrivate: false,
        };
        PhotoManager.uploadPhoto(
          photo,
          (result: PhotoUploadResult) => {
            if (!result.downloadURL || !result.token) {
              reject(new Error('Network error. Try again later.'));
              return;
            }
            if (!boomSwitchOn) {
              AppStorage.setItem(key, blob);
            }

            const token = PhotoManager.extractToken(result.downloadURL);
            const picmsg = `<bmd>${type} sender=${myinfo.fbUid} token=${token} key=${key} width=${width} height=${height}`;
            ChatDataManager.sendBoom(uid, picmsg, allocated);
            sendChatNotification(no, '[' + BResource.getCommon(no.locale, 'image') + ']');

            sendPictureLog(nudityObjState);
            sendChatPictureLog(nudityObjState, token, fromNudge.current, boomSwitchOn);
            resolve();
          },
          () => {}
        );
      });
    });
  };

  const purchaseBoom = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      ChatDataManager.purchase(boomProduct, boomPrice, data.uid, (result: boolean) => {
        if (result) {
          resolve();
        } else {
          reject(new Error('Purchase error'));
        }
      });
    });
  };

  // 업로드 버튼 클릭 핸들러
  const handleUpload = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      if (boomSwitchOn) {
        await purchaseBoom();
      }
      await uploadPicture();
      close('PictureUpload');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      CommonUI.showMessage('error', errorMessage);
      // purchaseBoom 실패 시에는 close 하지 않음
      // uploadPicture 실패 시에만 close
      if (errorMessage !== 'Purchase error') {
        close('PictureUpload');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    boomSwitchOn,
    boomPrice,
    handleUpload,
    handleBoomSwitchChange,
  };
};

export default usePictureUpload;
